// ── Polymarket US Org Keys Panel ────────────────────────────────
//
// Sibling of OrgKeysPanel / PolymarketOrgKeysPanel for Polymarket US (#987),
// with the same Add Key / Enable / Disable grammar: a US API key is an access
// key plus an Ed25519 private key, so it is entered like any other org key.
// The relay stores one live key per US account row; "Add Key" therefore
// reuses a keyless pooled row or creates one, attaches the key and enables it
// in a single action. Like a K/P key it is live on the next request once
// enabled: the relay connects its worker at once, and each row's badge shows
// the worker's actual state (active / connecting / not connected / disabled).
//
// Keys rotate like K/P keys: Disable, then Revoke (which also disables), then
// "Add key" on the revoked row binds a replacement for the SAME Polymarket US
// account. The relay records it as the old key's successor, so the account
// keeps its opening holdings, orders and settlement credits. A US key does not
// identify its venue account, so the manager's choice of row is the basis that
// the new key is for the same account, as with a K/P org key.
//
// Trader access is not edited here. Like Kalshi and Polymarket, it follows
// each member's role, org-key mode and the "Allow new Polymarket US buys" rule
// in Edit Limits; each key lists the result read-only under "Trader access".
//
// Retail keys leave component state when saving starts. Institutional setup
// holds its bounded file in the mounted form for discovery, then clears it
// before the disabled-account save. Neither path persists keys in browser
// storage, puts them in a URL, or logs them.

import { useEffect, useState } from 'react';
import { AlertTriangle, Ban, Landmark, Loader2, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { apiFetch } from '../../stores/authStore';
import { ConfirmModal } from '../../components/atoms/Modal';
import { HelpPopover } from '../../components/molecules/HelpPopover';
import { InstitutionalUsAccountSetup } from './InstitutionalUsAccountSetup';
import { discoverInstitutionalConnection } from '@/lib/polymarketUs/institutionalDiscoveryClient';
import { checkInstitutionalReference } from '@/lib/polymarketUs/institutionalReferenceClient';
import {
  hasLiveUsCredential,
  summarizeUsAccess,
  usActivationBadge,
  usActivationLines,
  usScanStatusLine,
  type UsAccountScanStatus,
  type UsActivationTone,
  type UsOrgAccount,
  type UsOrgAccountsPayload,
  type UsScanStatusPayload,
} from './polymarketUsOrgAccounts';

interface PolymarketUsOrgAccountsPanelProps {
  orgQueryParam?: string;
  embedded?: boolean;
  className?: string;
}

type Loaded = { org: string; payload: UsOrgAccountsPayload | null; error: string | null };
type CredentialDraft = { org: string; accountId: string; accessKey: string; privateKey: string };
/** The header "Add Key" form: no account yet, one is chosen on submit. */
type NewKeyDraft = { org: string; accessKey: string; privateKey: string; protocol?: 'retail' | 'institutional' };
type Scoped<T> = { org: string; value: T };
/** Worker evidence per account (#1245); a failed read only hides the lines. */
type ScanLoaded = { org: string; payload: UsScanStatusPayload | null };

const TONE_CLASS: Record<UsActivationTone, string> = {
  ok: 'text-emerald-500 dark:text-emerald-400',
  pending: 'text-amber-600 dark:text-amber-300',
  warn: 'text-red-500 dark:text-red-400',
  muted: 'text-muted-foreground',
};

/** Same palette as the K/P CredentialStateBadge. */
const BADGE_CLASS: Record<UsActivationTone, string> = {
  ok: 'bg-emerald-500/10 text-emerald-500 dark:text-emerald-400',
  pending: 'bg-amber-500/10 text-amber-500 dark:text-amber-400',
  warn: 'bg-red-500/10 text-red-500 dark:text-red-400',
  muted: 'bg-secondary text-muted-foreground',
};

const API = '/api/manager/polymarket-us';

async function readJson(res: Response): Promise<Record<string, unknown>> {
  try {
    const data: unknown = await res.json();
    return data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

function errorText(data: Record<string, unknown>, fallback: string): string {
  return typeof data.error === 'string' ? data.error : fallback;
}

function maskedKey(last4: string | null): string {
  return last4 ? `••••${last4}` : '••••';
}

export function PolymarketUsOrgAccountsPanel({
  orgQueryParam = '',
  embedded = false,
  className,
}: PolymarketUsOrgAccountsPanelProps) {
  // Every piece of state that belongs to one organization is stamped with the
  // org it was produced for and ignored under any other, so an admin switching
  // orgs never sees (or submits) the previous org's accounts or drafts.
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [scan, setScan] = useState<ScanLoaded | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [actionError, setActionError] = useState<Scoped<string> | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [newKey, setNewKey] = useState<NewKeyDraft | null>(null);
  const [credentialDraft, setCredentialDraft] = useState<CredentialDraft | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<Scoped<UsOrgAccount> | null>(null);
  /** The header Add Key is waiting for the manager to confirm a second account. */
  const [confirmSeparate, setConfirmSeparate] = useState<Scoped<true> | null>(null);
  const [institutionalTarget, setInstitutionalTarget] = useState<Scoped<string> | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(`${API}/accounts${orgQueryParam}`);
        const data = await readJson(res);
        if (cancelled) return;
        if (!res.ok) {
          setLoaded({ org: orgQueryParam, payload: null, error: errorText(data, 'Failed to load Polymarket US accounts') });
        } else {
          setLoaded({ org: orgQueryParam, payload: data as unknown as UsOrgAccountsPayload, error: null });
        }
      } catch {
        if (!cancelled) setLoaded({ org: orgQueryParam, payload: null, error: 'Network error' });
      }
    })();
    return () => { cancelled = true; };
  }, [orgQueryParam, reloadKey]);

  // The account worker's activity-scan evidence (#1245), read beside the list
  // and refreshed with it. It never blocks or fails the key list.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(`${API}/accounts/scan-status${orgQueryParam}`);
        const data = await readJson(res);
        if (cancelled) return;
        const ok = res.ok && Array.isArray(data.accounts) && typeof data.readAt === 'string';
        setScan({ org: orgQueryParam, payload: ok ? (data as unknown as UsScanStatusPayload) : null });
      } catch {
        if (!cancelled) setScan({ org: orgQueryParam, payload: null });
      }
    })();
    return () => { cancelled = true; };
  }, [orgQueryParam, reloadKey]);

  const current = loaded?.org === orgQueryParam ? loaded : null;
  const payload = current?.payload ?? null;
  const members = payload?.members ?? [];
  const accounts = payload?.accounts ?? [];
  const error = actionError?.org === orgQueryParam ? actionError.value : null;
  const newKeyDraft = newKey?.org === orgQueryParam ? newKey : null;
  const retailAccounts = accounts.filter((a) => a.protocol !== 'institutional');
  // A rejected connection can leave a disabled shell. Resume that shell
  // after closing/reopening the form instead of accumulating empty accounts.
  const institutionalKeyless = accounts.find((a) => a.protocol === 'institutional'
    && a.ownership === 'pooled' && !a.credential && !a.institutional) ?? null;
  const replacing = retailAccounts.find((a) => a.ownership === 'pooled' && a.credential?.revoked === true) ?? null;
  // Like a K/P Add Key (one account per org), a new org key goes to the org's
  // existing account when it has no live key: a revoked key is replaced there
  // (its opening, holdings and settlements carry over), else an unkeyed row.
  const keyless = replacing ?? retailAccounts.find((a) => a.ownership === 'pooled' && !a.credential) ?? null;
  const liveOrgKey = retailAccounts.find((a) => a.ownership === 'pooled' && hasLiveUsCredential(a)) ?? null;
  // The org already trades on a live key and the header key cannot replace
  // it: saving makes a SEPARATE account, which the manager confirms first (the
  // P panel's findPolyAccountConflict callout, in this panel's grammar).
  const separateAccount = liveOrgKey !== null && replacing === null;
  const confirmingSeparate = confirmSeparate?.org === orgQueryParam;
  const draft = credentialDraft?.org === orgQueryParam ? credentialDraft : null;
  const revoking = revokeTarget?.org === orgQueryParam ? revokeTarget.value : null;
  const memberName = (id: string) => members.find((m) => m.id === id)?.username ?? null;
  const scanPayload = scan?.org === orgQueryParam ? scan.payload : null;
  const scanFor = (id: string) => scanPayload?.accounts.find((a) => a.accountId === id) ?? null;

  const upsertAccount = (org: string, account: UsOrgAccount) => {
    setLoaded((prev) => {
      if (!prev || prev.org !== org || !prev.payload) return prev;
      const exists = prev.payload.accounts.some((a) => a.id === account.id);
      const next = exists
        ? prev.payload.accounts.map((a) => (a.id === account.id ? account : a))
        : [...prev.payload.accounts, account];
      return { ...prev, payload: { ...prev.payload, accounts: next } };
    });
  };

  /** One write, stamped with the org it was issued for. Returns the updated
   * account on success; records a visible error otherwise. */
  const write = async (key: string, method: string, path: string, body?: unknown): Promise<UsOrgAccount | null> => {
    const org = orgQueryParam;
    setBusyId(key);
    setActionError(null);
    try {
      const res = await apiFetch(`${API}${path}${org}`, {
        method,
        ...(body === undefined ? {} : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
      });
      const data = await readJson(res);
      if (!res.ok) {
        setActionError({ org, value: errorText(data, `Request failed (${res.status})`) });
        return null;
      }
      const account = data.account as UsOrgAccount | undefined;
      if (account) upsertAccount(org, account);
      return account ?? null;
    } catch {
      setActionError({ org, value: 'Network error' });
      return null;
    } finally {
      setBusyId(null);
    }
  };

  /** Add Key: pick (or create) a keyless pooled row, attach the key, enable it.
   * Reusing a keyless row means a refused key never leaves orphan rows. The
   * secret leaves component state before the first request starts. */
  const addKey = async (confirmed: boolean) => {
    if (!newKeyDraft || busyId || !payload) return;
    const accessKey = newKeyDraft.accessKey.trim();
    const privateKey = newKeyDraft.privateKey.trim();
    if (!accessKey || !privateKey) return;
    if (separateAccount && !confirmed) {
      setConfirmSeparate({ org: orgQueryParam, value: true });
      return;
    }
    setNewKey({ ...newKeyDraft, privateKey: '' });
    const target = keyless ?? await write('add-key', 'POST', '/accounts', { ownership: 'pooled' });
    if (!target) return;
    const keyed = await write('add-key', 'POST', `/accounts/${target.id}/credential`, { accessKey, privateKey });
    if (!keyed) return;
    setNewKey(null);
    // A new org key is enabled like a K/P key; the activation line still says
    // when this relay will actually trade with it.
    if (!keyed.enabled) await write(keyed.id, 'PATCH', `/accounts/${keyed.id}`, { enabled: true });
  };

  const submitCredential = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft || busyId) return;
    const accessKey = draft.accessKey.trim();
    const privateKey = draft.privateKey.trim();
    if (!accessKey || !privateKey) return;
    // Clear the secret from state before the request starts; it lives only in
    // this handler's local for the duration of the call.
    setCredentialDraft({ ...draft, privateKey: '' });
    const account = await write(draft.accountId, 'POST', `/accounts/${draft.accountId}/credential`, { accessKey, privateKey });
    if (account) setCredentialDraft(null);
  };

  const Wrapper = embedded ? 'div' : 'section';
  const wrapperClassName = embedded
    ? `min-w-0 ${className ?? ''}`
    : `bg-card border-border rounded-lg border p-4 ${className ?? 'mb-6'}`;
  const enabledCount = accounts.filter((a) => a.enabled).length;

  return (
    <Wrapper className={wrapperClassName}>
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1.5">
          <div className="flex shrink-0 items-center gap-2">
            <Landmark size={16} className="text-muted-foreground" />
            <h3 className="text-sm font-semibold">Org Polymarket US Keys</h3>
            <span className="text-muted-foreground text-[11px]">
              ({accounts.length === 0 ? 'none' : `${enabledCount}/${accounts.length} enabled`})
            </span>
          </div>
          <button
            onClick={() => setReloadKey((k) => k + 1)}
            title="Re-read which keys the running relay is trading with"
            className="text-muted-foreground hover:text-foreground inline-flex shrink-0 items-center gap-1 rounded px-2 py-1 text-[11px] hover:bg-muted"
          >
            <RefreshCw size={12} /> Refresh status
          </button>
          {separateAccount && (
            /* The P panel's header-inline amber line (Brendan, Jul-9: brief, not
               a block callout): a second header key is a second account. */
            <div
              className="flex items-center gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-2.5 py-1 text-[10px] leading-snug text-amber-200/90 light:text-amber-700"
              title={`Key ${maskedKey(liveOrgKey?.credential?.accessKeyLast4 ?? null)} is live on this org's Polymarket US account. Saving another key here creates a separate Polymarket US account beside it (its own holdings, orders and balance); to replace the key on the same account, disable and revoke it, then use that row's Add key.`}
            >
              <AlertTriangle size={12} className="shrink-0 text-amber-100 light:text-amber-700" />
              <span>
                <span className="font-medium text-amber-100 light:text-amber-700">One live org key</span> — Add Key here
                makes a separate Polymarket US account.
              </span>
            </div>
          )}
        </div>
        <button
          onClick={() => setNewKey(newKeyDraft ? null : { org: orgQueryParam, accessKey: '', privateKey: '' })}
          disabled={!payload || busyId !== null}
          className="flex shrink-0 items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-500 disabled:opacity-50"
        >
          <Plus size={14} /> {newKeyDraft ? 'Cancel' : 'Add Key'}
        </button>
      </div>

      <div className="text-muted-foreground mb-3 flex flex-wrap items-center gap-x-2 text-[11px]">
        <span>Manage your organization&apos;s Polymarket US keys.</span>
        <HelpPopover label="Polymarket US key setup details" title="Polymarket US keys"
          trigger="Setup details" triggerClassName="underline decoration-dotted underline-offset-4 hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2">
          A Polymarket US key is the account&apos;s API access key plus its Ed25519 private key. The private key is
          checked with Polymarket US before saving, encrypted at rest and never shown to the browser again after upload.
          An enabled key starts connecting right away; its badge shows whether the relay is trading on it now.
          Institutional API connections use a separate RSA key and provisioned account identity; they save disabled.
        </HelpPopover>
      </div>

      {(error ?? current?.error) && (
        <div className="mb-3 rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-400">{error ?? current?.error}</div>
      )}

      {newKeyDraft && (
        <label className="mb-2 block text-[11px] text-muted-foreground">
          Connection type
          <select aria-label="Connection type" value={newKeyDraft.protocol ?? 'retail'} disabled={busyId !== null}
            onChange={(e) => setNewKey({ org: orgQueryParam, protocol: e.target.value as 'retail' | 'institutional', accessKey: '', privateKey: '' })}
            className="bg-card border-border ml-2 rounded-md border px-2 py-1 text-xs">
            <option value="retail">Retail API</option><option value="institutional">Institutional API</option>
          </select>
        </label>
      )}
      {newKeyDraft?.protocol === 'institutional' && (
        <InstitutionalUsAccountSetup key={`new-institutional-${orgQueryParam}`} account={institutionalKeyless} busy={busyId !== null || !payload}
          discoveryAvailable={payload?.institutionalVerificationAvailable === true}
          referenceAvailable={payload?.institutionalReferenceAvailable === true}
          checkReference={(request, signal) => checkInstitutionalReference(request, signal, orgQueryParam)}
          discover={(request, signal) => discoverInstitutionalConnection(request, signal, orgQueryParam)}
          write={write} onClose={() => setNewKey(null)} />
      )}
      {newKeyDraft && newKeyDraft.protocol !== 'institutional' && (
        <form onSubmit={(e) => { e.preventDefault(); void addKey(false); }}
          className="bg-muted/50 border-border mb-3 space-y-2 rounded-md border p-3">
          {replacing && (
            <p className="text-muted-foreground text-[11px]">
              This replaces the org&apos;s revoked key {maskedKey(replacing.credential?.accessKeyLast4 ?? null)}. The new
              key must be for the same Polymarket US account; its holdings, orders and settlements carry over.
            </p>
          )}
          <KeyFields
            idPrefix="us-new"
            draft={newKeyDraft}
            onChange={(next) => setNewKey({ ...next, org: orgQueryParam })}
          />
          <button
            type="submit"
            disabled={busyId !== null || !newKeyDraft.accessKey.trim() || !newKeyDraft.privateKey.trim()}
            className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-500 disabled:opacity-50"
          >
            {busyId === 'add-key' && <Loader2 size={12} className="animate-spin" />} Save Key
          </button>
        </form>
      )}

      {!current ? (
        <div className="flex justify-center py-4">
          <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
        </div>
      ) : payload && accounts.length === 0 ? (
        <p className="text-muted-foreground py-2 text-center text-xs">
          No Polymarket US org key yet. Add one to let org traders place Polymarket US orders via the relay.
        </p>
      ) : (
        <div className="space-y-2">
          {accounts.map((account) => (
            <div key={account.id}>
            <AccountCard
              account={account}
              institutionalVerificationAvailable={payload?.institutionalVerificationAvailable === true}
              scan={scanPayload ? scanFor(account.id) : null}
              scanReadAt={scanPayload?.readAt ?? null}
              busyId={busyId}
              memberName={memberName}
              draft={draft?.accountId === account.id ? draft : null}
              onToggleEnabled={() => write(account.id, 'PATCH', `/accounts/${account.id}`, { enabled: !account.enabled })}
              onStartCredential={() =>
                account.protocol === 'institutional'
                  ? setInstitutionalTarget({ org: orgQueryParam, value: account.id })
                  : setCredentialDraft({ org: orgQueryParam, accountId: account.id, accessKey: '', privateKey: '' })}
              onCancelCredential={() => setCredentialDraft(null)}
              onDraftChange={(next) => setCredentialDraft({ ...next, org: orgQueryParam, accountId: account.id })}
              onSubmitCredential={submitCredential}
              onRevoke={() => setRevokeTarget({ org: orgQueryParam, value: account })}
              onVerify={() => write(account.id, 'POST', `/accounts/${account.id}/institutional-connection/verify`)}
            />
            {institutionalTarget?.org === orgQueryParam && institutionalTarget.value === account.id && (
              <InstitutionalUsAccountSetup key={`${orgQueryParam}-${account.id}`} account={account} busy={busyId !== null}
                discoveryAvailable={payload?.institutionalVerificationAvailable === true}
                referenceAvailable={payload?.institutionalReferenceAvailable === true}
                checkReference={(request, signal) => checkInstitutionalReference(request, signal, orgQueryParam)}
                discover={(request, signal) => discoverInstitutionalConnection(request, signal, orgQueryParam)}
                write={write} onClose={() => setInstitutionalTarget(null)} />
            )}
            </div>
          ))}
        </div>
      )}

      <ConfirmModal
        isOpen={confirmingSeparate}
        onClose={() => setConfirmSeparate(null)}
        onConfirm={() => { void addKey(true); }}
        title="Add a second Polymarket US key"
        message={`Key ${maskedKey(liveOrgKey?.credential?.accessKeyLast4 ?? null)} is live on this org's Polymarket US account. Saving this key creates a separate Polymarket US account beside it, enabled at once, with its own holdings, orders and balance; traders can be granted either. To replace the key on the same account instead, disable and revoke it, then use the row's Add key to replace a key on the same account.`}
        confirmLabel="Add separate account"
      />
      <ConfirmModal
        isOpen={!!revoking}
        onClose={() => setRevokeTarget(null)}
        onConfirm={() => {
          if (revoking) void write(revoking.id, 'POST', `/accounts/${revoking.id}/credential/revoke`);
        }}
        title="Revoke Polymarket US Key"
        message={revoking?.protocol === 'institutional'
          ? 'Revoke this institutional connection? Its key can no longer be used, and its verification is withdrawn. Saved activity is retained. A replacement must belong to the same provisioned trading account; replacement and verification do not enable trading.'
          : `Revoke key ${maskedKey(revoking?.credential?.accessKeyLast4 ?? null)}? The account is disabled in the same step, so no new US orders can use it. Orders already sent keep their records. A revoked key can never be restored or registered again; to resume, add a new key for the same Polymarket US account on this row and re-enable it. The account's holdings, orders and settlements carry over to the new key.`}
        confirmLabel="Revoke Key"
        variant="danger"
      />
    </Wrapper>
  );
}

interface AccountCardProps {
  account: UsOrgAccount;
  institutionalVerificationAvailable: boolean;
  scan: UsAccountScanStatus | null;
  scanReadAt: string | null;
  busyId: string | null;
  memberName: (id: string) => string | null;
  draft: CredentialDraft | null;
  onToggleEnabled: () => void;
  onStartCredential: () => void;
  onCancelCredential: () => void;
  onDraftChange: (draft: CredentialDraft) => void;
  onSubmitCredential: (e: React.FormEvent) => void;
  onRevoke: () => void;
  onVerify: () => void;
}

function AccountCard({
  account, institutionalVerificationAvailable, scan, scanReadAt, busyId, memberName, draft, onToggleEnabled, onStartCredential,
  onCancelCredential, onDraftChange, onSubmitCredential, onRevoke, onVerify,
}: AccountCardProps) {
  const personal = account.ownership === 'personal';
  const institutional = account.protocol === 'institutional';
  const live = hasLiveUsCredential(account);
  const busy = institutional ? busyId !== null : busyId === account.id;
  const lines = usActivationLines(account, memberName);
  const badge = usActivationBadge(account);
  const ownerLabel = personal
    ? `Personal · ${account.owner?.username ?? memberName(account.owner?.userId ?? '') ?? 'former member'}`
    : institutional ? 'Institutional connection' : 'Org key';
  const access = summarizeUsAccess(account);
  // A revoked row takes a replacement key for the same venue account.
  const rotating = account.credential?.revoked === true;
  // Worker evidence only for a row that has (or had) a key; one line, never wrapping.
  const scanLine = !institutional && scan && scanReadAt && account.credential ? usScanStatusLine(scan, scanReadAt) : null;
  // The badge already names the connection state. Keep additional blocking
  // states in sight; supporting worker/identity details belong on demand.
  const notices = lines.filter((line) => !line.text.startsWith('Runs as ') && line.text !== badge.label);

  return (
    <div className="border-border/60 bg-muted/30 rounded-md border px-3 py-2" data-testid={`us-account-${account.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="text-muted-foreground flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-[11px]">
          <span className="text-foreground text-xs font-medium">{ownerLabel}</span>
          <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${BADGE_CLASS[badge.tone]}`}
            data-testid={`us-account-status-${account.id}`}>
            {badge.label}
          </span>
          <span className="text-muted-foreground/40">·</span>
          {account.credential ? (
            <>
              <span className="min-w-0 break-all font-mono">
                {institutional ? account.institutional?.accountName ?? 'Connection saved'
                  : `${account.credential.revoked ? 'Revoked key ' : 'Key '}${maskedKey(account.credential.accessKeyLast4)}`}
              </span>
              {account.credential.createdAt && (
                <>
                  <span className="text-muted-foreground/40">·</span>
                  <span>Added {new Date(account.credential.createdAt).toLocaleDateString()}</span>
                </>
              )}
            </>
          ) : (
            <span>No key</span>
          )}
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
          {(!live || institutional) && !draft && (
            <button
              onClick={onStartCredential}
              disabled={busy}
              title={rotating ? 'Replace the revoked key with a new key for the same Polymarket US account' : undefined}
              className="text-muted-foreground inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] hover:bg-blue-500/10 hover:text-blue-400 disabled:opacity-50"
            >
              <Plus size={12} /> {institutional ? live ? 'Replace connection' : 'Set up connection' : 'Add key'}
            </button>
          )}
          {institutional && live && <button onClick={onVerify} disabled={busy || !institutionalVerificationAvailable}
            aria-describedby={!institutionalVerificationAvailable ? `us-verification-unavailable-${account.id}` : undefined}
            className="text-muted-foreground rounded px-2 py-1 text-[11px] hover:bg-muted disabled:opacity-50">Verify connection</button>}
          {!institutional && (account.enabled || live) && (
            <button
              onClick={onToggleEnabled}
              disabled={busy}
              title={account.enabled ? 'Disable key' : 'Enable key'}
              className="text-muted-foreground inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] hover:bg-muted hover:text-foreground disabled:opacity-50"
            >
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Ban size={12} />}
              {account.enabled ? 'Disable' : 'Enable'}
            </button>
          )}
          {/* Like Delete on a K/P key, revocation is offered once the key is
              disabled, with or without a recorded opening. */}
          {live && !account.enabled && (
            <button
              onClick={onRevoke}
              disabled={busy}
              title="Permanently revoke this key"
              className="text-muted-foreground inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] hover:bg-red-500/10 hover:text-red-400 disabled:opacity-50"
            >
              <Trash2 size={12} />
              Revoke key
            </button>
          )}
        </div>
      </div>

      {institutional && live && !institutionalVerificationAvailable && (
        <p id={`us-verification-unavailable-${account.id}`} className="text-muted-foreground mt-1.5 text-[11px]">
          Verification is unavailable on this site.
        </p>
      )}
      {notices.length > 0 && (
        <ul className="mt-1.5 space-y-0.5 text-[11px]" aria-label="Activation">
          {notices.map((line) => (
            <li key={line.text} className={TONE_CLASS[line.tone]}>
              <span className="font-medium">{line.text}</span>
            </li>
          ))}
        </ul>
      )}

      {scanLine && scanLine.tone !== 'ok' && (
        <p
          className={`mt-1 truncate text-[11px] ${TONE_CLASS[scanLine.tone]}`}
          title={scanLine.title}
          data-testid={`us-account-scan-${account.id}`}
        >
          {scanLine.text}
        </p>
      )}

      {(lines.length > 0 || scanLine) && (
        <div className="mt-1 text-[11px] text-muted-foreground">
          <HelpPopover label={`${ownerLabel} connection details`} title={`${ownerLabel}: connection details`}
            trigger={scanLine?.tone === 'ok' ? 'Last activity check clear · Details' : 'Connection details'}
            triggerClassName="underline decoration-dotted underline-offset-4 hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
            testId={`us-account-details-${account.id}`}>
            <ul className="space-y-1">
              {lines.map((line) => <li key={line.text}>{line.text}{line.detail ? ` — ${line.detail}` : ''}</li>)}
            </ul>
            {scanLine && <>
              <p className="mt-2">{scanLine.text}</p>
              <p className="mt-1 whitespace-pre-line text-[11px]">{scanLine.title}</p>
            </>}
          </HelpPopover>
        </div>
      )}

      {draft && (
        <form onSubmit={onSubmitCredential} className="bg-muted/50 border-border mt-2 space-y-2 rounded-md border p-3">
          {rotating && (
            <p className="text-muted-foreground text-[11px]" data-testid={`us-rotate-note-${account.id}`}>
              The new key must be for the same Polymarket US account as the revoked key. It keeps this account&apos;s
              holdings, orders and settlements. The key itself cannot show which account it belongs to, so check it
              before saving; for a different account, use Add Key at the top instead.
            </p>
          )}
          <KeyFields idPrefix={`us-${account.id}`} draft={draft} onChange={(next) => onDraftChange({ ...draft, ...next })} />
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={busy || !draft.accessKey.trim() || !draft.privateKey.trim()}
              className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-500 disabled:opacity-50"
            >
              {busy && <Loader2 size={12} className="animate-spin" />} Save key
            </button>
            <button type="button" onClick={onCancelCredential} className="text-muted-foreground rounded-md px-3 py-1.5 text-xs hover:bg-muted">
              Cancel
            </button>
          </div>
        </form>
      )}

      <details className="mt-1.5 text-[11px]">
        <summary className="text-muted-foreground hover:text-foreground cursor-pointer select-none">
          {/* A disabled key places nothing, so its count is what access would be once enabled (#1111). */}
          {account.enabled
            ? `Trader access (${access.buyAndSell.length} can buy)`
            : `Trader access when enabled (${access.buyAndSell.length} could buy)`}
        </summary>
        <TraderAccess personal={personal} access={access} />
      </details>
    </div>
  );
}

/** Access key + private key inputs shared by the header and per-key forms. */
function KeyFields<T extends { accessKey: string; privateKey: string }>({ idPrefix, draft, onChange }: {
  idPrefix: string;
  draft: T;
  onChange: (next: T) => void;
}) {
  return (
    <>
      <div>
        <label htmlFor={`${idPrefix}-access`} className="text-muted-foreground mb-1 block text-[11px]">Access key</label>
        <input
          id={`${idPrefix}-access`}
          value={draft.accessKey}
          onChange={(e) => onChange({ ...draft, accessKey: e.target.value })}
          autoComplete="off"
          className="bg-card border-border text-foreground w-full rounded-md border px-2 py-1 font-mono text-[10px] focus:border-blue-500 focus:outline-none"
        />
      </div>
      <div>
        <label htmlFor={`${idPrefix}-private`} className="text-muted-foreground mb-1 block text-[11px]">Private key (base64 Ed25519)</label>
        <input
          id={`${idPrefix}-private`}
          type="password"
          value={draft.privateKey}
          onChange={(e) => onChange({ ...draft, privateKey: e.target.value })}
          autoComplete="off"
          className="bg-card border-border text-foreground w-full rounded-md border px-2 py-1 font-mono text-[10px] focus:border-blue-500 focus:outline-none"
        />
      </div>
    </>
  );
}

const ACCESS_ROWS: { key: keyof ReturnType<typeof summarizeUsAccess>; label: string }[] = [
  { key: 'buyAndSell', label: 'Buy and sell' },
  { key: 'sellOnly', label: 'Sell and cancel only' },
  { key: 'desk', label: 'Desk view and cancel' },
  { key: 'noAccess', label: 'No access' },
];

/** Read-only: who can trade this key and who sees the desk. Names wrap inside
 * a full-width row rather than being hidden behind a hover. */
function TraderAccess({ personal, access }: { personal: boolean; access: ReturnType<typeof summarizeUsAccess> }) {
  const rows = ACCESS_ROWS.filter((row) => access[row.key].length > 0);
  return (
    <div className="mt-1.5">
      {rows.length === 0 ? (
        <p className="text-muted-foreground">No current members.</p>
      ) : (
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5" aria-label="Trader access">
          {rows.map((row) => (
            <div key={row.key} className="contents">
              <dt className="text-muted-foreground whitespace-nowrap">{row.label}</dt>
              <dd className="text-foreground min-w-0 break-words">{access[row.key].join(', ')}</dd>
            </div>
          ))}
        </dl>
      )}
      <p className="text-muted-foreground mt-1">
        {personal
          ? 'A personal key serves only its owner, never desk-wide, and is not offered to trader-role members. '
          : 'Org-key members trade here; members in personal-key mode do not. Managers and admins see and cancel the desk. '}
        Change a member&apos;s US buys with &ldquo;Allow new Polymarket US buys&rdquo; in Edit Limits.
      </p>
      <p className="text-muted-foreground mt-0.5 text-[11px]">
        US buys count toward the trader's contract and position caps together with their Kalshi and
        international Polymarket orders.
      </p>
    </div>
  );
}
